import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { adjustComment, adjustTask } from '@valhalla/data/entities';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { IApiResponse } from '../api-response';
import ApiVersion from '../api-versions';
import { DataHttpCommonService } from '../data-http-common.service';
import {
	FeedCommentsType,
	FeedsDataHttpService,
	FeedTasksType,
	ICommentsFiltersBySubcat,
	IFeedReadAllCriteria,
	IGetFeedCommentsCriteria,
	IGetFeedCommentsResult,
	IGetFeedTasksCriteria,
	IGetFeedTasksResult,
} from './abstract';

@Injectable()
export class FeedsDataHttpServiceImpl implements FeedsDataHttpService {
	constructor(public readonly http: HttpClient, public readonly common: DataHttpCommonService) {}

	comments(criteria: IGetFeedCommentsCriteria): Observable<IGetFeedCommentsResult> {
		if (criteria.searchString && !criteria.searchType) {
			criteria.searchType = 'Contains';
		}
		const url = this.common.getApiUrl(`comments/lenta`);
		return this.http
			.post<IApiResponse<IGetFeedCommentsResult>>(url, {
				fromUserID: criteria.userId,
				limit: criteria.limit,
				offset: criteria.offset,
				subcatId: criteria.subcatId,
				catId: criteria.catId,
				lentaCommTypes: criteria.lentaCommTypes,
				commentId: criteria.commentId,
				limitBefore: criteria.limitBefore,
				limitAfter: criteria.limitAfter,
				searchString: criteria.searchString,
				searchType: criteria.searchType,
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.comments.forEach(adjustComment);
				})
			);
	}

	tasks(criteria: IGetFeedTasksCriteria, options?: Record<string, any>): Observable<IGetFeedTasksResult> {
		switch (criteria.feedType) {
			case FeedTasksType.all:
				return this.allTasks(criteria, options);
			case FeedTasksType.favorites:
				return this.favoritesTasks(criteria, options);
			case FeedTasksType.new:
				return this.newTasks(criteria, options);
			case FeedTasksType.owner:
				return this.ownerTasks(criteria, options);
			case FeedTasksType.performer:
				return this.performerTasks(criteria, options);
			case FeedTasksType.stage:
				return this.stageTasks(criteria, options);
			default:
				// tslint:disable-next-line:no-console
				console.warn(`Fallback for feedType='${criteria.feedType}' redirect to tasks/feeds api`);
				break;
		}

		const url = this.common.getApiUrl(`tasks/feeds`);
		return this.http
			.post<IApiResponse<IGetFeedTasksResult>>(url, {
				limit: criteria.limit,
				offset: criteria.offset,
				feedTypes: [criteria.feedType],
				filters: [],
				sorters: [{ sorterType: 'OrderedTime' }],
				showClosed: Boolean(criteria.showClosed),
				showLastComment: Boolean(criteria.showLastComment),
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.tasks.forEach(adjustTask);
				})
			);
	}

	tasksRawRequest(body: Record<string, any>): Observable<IGetFeedTasksResult> {
		const url = this.common.getApiUrl(`tasks/feedsBySP`);
		return this.http.post<IApiResponse<IGetFeedTasksResult>>(url, body).pipe(
			map(result => result.data),
			tap(data => {
				data.tasks.forEach(adjustTask);
			})
		);
	}

	read(criteria: IFeedReadAllCriteria): Observable<any> {
		switch (criteria.type) {
			case FeedCommentsType.all:
				return this.readAll();
			case FeedCommentsType.unread:
				return this.readAllUnread();
			case FeedCommentsType.myQuestions:
				return this.readAllMyQuestions();
			default:
				break;
		}

		// tslint:disable-next-line: no-console
		console.warn(`readAll not implemented for feed type '${criteria.type}'`);
		return EMPTY;
	}

	readSupport(type: FeedCommentsType): boolean {
		return [FeedCommentsType.all, FeedCommentsType.unread, FeedCommentsType.myQuestions].indexOf(type) !== -1;
	}

	getCommentsFiltersBySubcat(subcatId: number): Observable<ICommentsFiltersBySubcat> {
		const url = this.common.getApiUrl(`subcategories/${subcatId}/commentFilters`, ApiVersion.v10);
		return this.http.get<ICommentsFiltersBySubcat>(url).pipe(catchError(_ => of(null)));
	}

	setCommentsFiltersBySubcat(subcatId: number, filters: ICommentsFiltersBySubcat): Observable<any> {
		const url = this.common.getApiUrl(`subcategories/${subcatId}/commentFilters`, ApiVersion.v10);
		return this.http.post<any>(url, filters);
	}

	feedBySP(
		{ limit, offset, catIds, subcatIds }: IGetFeedTasksCriteria,
		options?: Record<string, any>
	): Observable<IGetFeedTasksResult> {
		const url = this.common.getApiUrl(`tasks/feedsBySP`);
		return this.http
			.post<IApiResponse<IGetFeedTasksResult>>(url, {
				resultRowsCount: limit,
				resultSetOffset: offset,
				catIds,
				subcatIds,
				...(options || {}),
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.tasks.forEach(adjustTask);
				})
			);
	}

	protected readAllMyQuestions() {
		const url = this.common.getEndpointUrl('/1FMain.aspx/MarkAllMyQuestionsAsAnswered');
		return this.http.post(url, null, {
			headers: {
				'content-type': 'application/json; charset=utf-8',
			},
		});
	}

	protected readAllUnread() {
		const url = this.common.getEndpointUrl('/1FMain.aspx/ReadAll');
		return this.http.post(url, {
			catID: null,
			count: 100,
			favsFolderId: null,
			filterCommentsSignatures: false,
			filterOnlyNew: false,
			groupId: null,
			maxCommentID: null,
			maxTaskTextLength: 1000,
			minCommentID: null,
			onlyFromUsers: false,
			onlyMine: false,
			onlySubscribed: false,
			orgUnitId: null,
			personalsType: null,
			showSystem: false,
			subcatID: null,
			type: 'unread',
		});
	}

	protected readAll() {
		const url = this.common.getEndpointUrl('/1FMain.aspx/ReadAll');
		return this.http.post(url, {
			catID: null,
			count: 100,
			favsFolderId: null,
			filterCommentsSignatures: false,
			filterOnlyNew: false,
			groupId: null,
			maxCommentID: null,
			maxTaskTextLength: 1000,
			minCommentID: null,
			onlyFromUsers: false,
			onlyMine: false,
			onlySubscribed: false,
			orgUnitId: null,
			personalsType: null,
			showSystem: false,
			subcatID: null,
			type: 'lenta',
		});
	}

	protected allTasks(
		{ limit, offset, catIds, subcatIds }: IGetFeedTasksCriteria,
		options?: Record<string, any>
	): Observable<IGetFeedTasksResult> {
		const url = this.common.getApiUrl(`tasks/feedsBySP`);
		return this.http
			.post<IApiResponse<IGetFeedTasksResult>>(url, {
				showLastComment: false,
				resultRowsCount: limit,
				resultSetOffset: offset,
				openOnlyF: true,
				closedOnlyF: false,
				favoritesOnlyF: false,
				taskHelperOnlyF: false,
				subscriberOnlyF: true,
				myTasksOnlyF: false,
				oneFMainVisibilityModeF: true,
				workNotStartedOnlyF: false,
				reversedOrderF: false,
				orderByCreatedTaskF: true,
				outLastCommentF: true,
				catIds,
				subcatIds,
				...(options || {}),
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.tasks.forEach(adjustTask);
				})
			);
	}

	protected ownerTasks(
		{ limit, offset, catIds, subcatIds }: IGetFeedTasksCriteria,
		options?: Record<string, any>
	): Observable<IGetFeedTasksResult> {
		const url = this.common.getApiUrl(`tasks/feedsBySP`);
		return this.http
			.post<IApiResponse<IGetFeedTasksResult>>(url, {
				showLastComment: false,
				resultRowsCount: limit,
				resultSetOffset: offset,
				openOnlyF: true,
				closedOnlyF: false,
				favoritesOnlyF: false,
				taskHelperOnlyF: false,
				subscriberOnlyF: false,
				myTasksOnlyF: true,
				oneFMainVisibilityModeF: true,
				workNotStartedOnlyF: false,
				reversedOrderF: false,
				orderByOrderedTimeF: true,
				outLastCommentF: true,
				catIds,
				subcatIds,
				...(options || {}),
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.tasks.forEach(adjustTask);
				})
			);
	}

	protected performerTasks(
		{ limit, offset, catIds, subcatIds }: IGetFeedTasksCriteria,
		options?: Record<string, any>
	): Observable<IGetFeedTasksResult> {
		const url = this.common.getApiUrl(`tasks/feedsBySP`);
		return this.http
			.post<IApiResponse<IGetFeedTasksResult>>(url, {
				showLastComment: false,
				resultRowsCount: limit,
				resultSetOffset: offset,
				openOnlyF: true,
				closedOnlyF: false,
				favoritesOnlyF: false,
				taskHelperOnlyF: true,
				subscriberOnlyF: false,
				myTasksOnlyF: false,
				oneFMainVisibilityModeF: false,
				workNotStartedOnlyF: false,
				reversedOrderF: false,
				orderByCreatedTaskF: false,
				orderByOrderedTimeF: true,
				outLastCommentF: true,
				catIds,
				subcatIds,
				...(options || {}),
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.tasks.forEach(adjustTask);
				})
			);
	}

	protected favoritesTasks(
		{ limit, offset, catIds, subcatIds }: IGetFeedTasksCriteria,
		options?: Record<string, any>
	): Observable<IGetFeedTasksResult> {
		const url = this.common.getApiUrl(`tasks/feedsBySP`);
		return this.http
			.post<IApiResponse<IGetFeedTasksResult>>(url, {
				showLastComment: false,
				resultRowsCount: limit,
				resultSetOffset: offset,
				openOnlyF: false,
				closedOnlyF: false,
				favoritesOnlyF: true,
				taskHelperOnlyF: false,
				subscriberOnlyF: false,
				myTasksOnlyF: false,
				oneFMainVisibilityModeF: false,
				workNotStartedOnlyF: false,
				reversedOrderF: false,
				orderByCreatedTaskF: true,
				orderByOrderedTimeF: false,
				outLastCommentF: true,
				catIds,
				subcatIds,
				...(options || {}),
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.tasks.forEach(adjustTask);
				})
			);
	}

	protected newTasks(
		{ limit, offset, catIds, subcatIds }: IGetFeedTasksCriteria,
		options?: Record<string, any>
	): Observable<IGetFeedTasksResult> {
		const url = this.common.getApiUrl(`tasks/feedsBySP`);
		return this.http
			.post<IApiResponse<IGetFeedTasksResult>>(url, {
				showLastComment: false,
				resultRowsCount: limit,
				resultSetOffset: offset,
				openOnlyF: false,
				closedOnlyF: false,
				favoritesOnlyF: false,
				taskHelperOnlyF: false,
				subscriberOnlyF: false,
				myTasksOnlyF: true,
				oneFMainVisibilityModeF: false,
				workNotStartedOnlyF: true,
				reversedOrderF: false,
				orderByCreatedTaskF: true,
				orderByOrderedTimeF: false,
				outLastCommentF: true,
				catIds,
				subcatIds,
				...(options || {}),
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.tasks.forEach(adjustTask);
				})
			);
	}

	protected stageTasks(
		{ limit, offset, catIds, subcatIds }: IGetFeedTasksCriteria,
		options?: Record<string, any>
	): Observable<IGetFeedTasksResult> {
		const url = this.common.getApiUrl(`tasks/feedsBySP`);
		return this.http
			.post<IApiResponse<IGetFeedTasksResult>>(url, {
				resultRowsCount: limit,
				resultSetOffset: offset,
				closedOnlyF: false,
				favoritesOnlyF: false,
				myTasksOnlyF: false,
				oneFMainVisibilityModeF: false,
				openOnlyF: false,
				orderByCreatedTaskF: true,
				outLastCommentF: true,
				reversedOrderF: true,
				showLastComment: false,
				subscriberOnlyF: false,
				taskHelperOnlyF: false,
				workNotStartedOnlyF: false,
				catIds,
				subcatIds,
				...(options || {}),
			})
			.pipe(
				map(result => result.data),
				tap(data => {
					data.tasks.forEach(adjustTask);
				})
			);
	}
}
